<script setup lang="ts">
import { useImage } from '@vueuse/core'
import { useBestFileVariant } from 'frontend/common/files/composables/useBestFileVariant'
import type { IFileResponseEntity } from 'frontend/common/files/types/i-file-response-entity'
import { computed } from 'vue'

interface Props {
  file: IFileResponseEntity
  hideDeleteIcon?: boolean
  isCurrent?: boolean
  imageTooltip?: unknown
  preferredVariantSize: [number, number] | null
}
const props = withDefaults(defineProps<Props>(), {
  preferredVariantSize: () => [64, 64],
})

interface Emits {
  (event: 'deleteClick'): void
  (event: 'imageClick'): void
}
const emit = defineEmits<Emits>()

const { getBestFileVariantBySize } = useBestFileVariant()

const imageUrl = computed(() => {
  return (
    getBestFileVariantBySize(
      props.file.variants,
      props.preferredVariantSize[0],
      props.preferredVariantSize[1],
    )?.url || props.file.url
  )
})

const isImage = computed(() => {
  return props.file.contentType?.length
    ? props.file.contentType.startsWith('image/')
    : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
        imageUrl?.value?.split('.')?.pop() || 'unknown',
      )
})

const { isReady } = useImage(() => {
  return {
    src: imageUrl.value,
  }
})

const loadingOverlayStyle = {
  animationDelay: `${Math.random() * 2}s`,
}
</script>

<template lang="pug">
.file-thumbnail(:class="{ 'is-current': isCurrent }")
  template(v-if="isImage")
    img.image(v-if="isReady" :src="imageUrl" @click="emit('imageClick')" v-tooltip="imageTooltip")
    .loading-overlay(v-else :style="loadingOverlayStyle")
  template(v-else)
    .image.no-preview(@click="emit('imageClick')" v-tooltip="imageTooltip")
      i.bi-file-earmark
      span.file-name(v-if="!isImage") {{ file.fileName }}
  i.delete-icon.mi-outlined.mi-delete(v-if="!hideDeleteIcon" @click="emit('deleteClick')")
  slot
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables.scss' as themeVariables;

.file-thumbnail {
  aspect-ratio: 1/1;
  width: 100%;
  position: relative;
  border-radius: themeVariables.$borderRadius;
  overflow: hidden;
  background: map.get(themeVariables.$colors, containerHighest);
  cursor: pointer;
  will-change: opacity, transform;

  &:hover {
    opacity: 0.85;
    transform: scale(0.98);
  }

  &.is-current {
    border: 3px solid map.get(themeVariables.$colors, primary);
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-direction: column;
  padding: 4px;
  .file-name {
    font-size: 12px;
    word-break: break-word;
    max-height: calc(100% - 30px);
    overflow: hidden;
    text-overflow: ellipsis;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3; /* Number of lines to show */
    line-clamp: 3;
  }
}

.delete-icon {
  color: map.get(themeVariables.$colors, error);
  position: absolute;
  bottom: 0;
  right: 0;
  background: map.get(themeVariables.$colors, containerHigh);
  border-top-left-radius: themeVariables.$borderRadius;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  padding-top: 2px;
  padding-left: 2px;
  cursor: pointer;
}

@keyframes blink-animation {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.loading-overlay {
  width: 100%;
  height: 100%;
  background: map.get(themeVariables.$colors, outlineVariant);
  animation: blink-animation 2s infinite;
  pointer-events: none;
}
</style>
